import { AfterContentInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { DateService } from 'src/app/shared/services/date.service';
import { GetOpenEnrollmentRule } from '../../family-form/types/qualifying-life-event.response.interface';
import { Subscription, Observable } from 'rxjs';
import { setProxyValue } from '../../plan-enrollment/individual-plan-enrollment-form/configs/reducers/special-enrollment-circumstance.reducer';
import { defaultLifeEventProxyConnectPath, submittedAttemptProxyConnectPath } from 'src/app/angular-elements/shared/interfaces/special-enrollment-circumstances.interface';
import { FamilyFormService } from '../../family-form/services/family-form.service';
import { SepLifeEventService } from 'src/app/shared/services/sep-life-event.service';
import { setFetchFormConfig } from 'src/app/shared/reducers/sep-life-event-form.reducer';

interface ComponentFormControls {
  lifeEvent: AbstractControl;
}
@FormComponentLookup('special-enrollment-event-selector-proxy')
@Component({
    selector: 'app-special-enrollment-event-selector-proxy',
    templateUrl: './special-enrollment-event-selector-proxy.component.html',
    styleUrls: ['./special-enrollment-event-selector-proxy.component.scss'],
    standalone: false
})
export class SpecialEnrollmentEventSelectorProxyComponent extends FormEntryComponentClass implements OnInit, AfterContentInit, OnDestroy {

  eventForm: FormGroup;
  eventFormSubscription: Subscription;
  qualifyingLifeEvents: GetOpenEnrollmentRule[] = [];
  proxyLifeEventConnectPath: string;

  submitAttemptedObservable$: Observable<boolean>;
  submitAttemptedSubscription: Subscription;

  // qualifyingLifeEvents: GetOpenEnrollmentRule[];

  constructor(
    private dateService: DateService,
    private fb: FormBuilder,
    private familyFormService: FamilyFormService,
    private sepLifeEventService: SepLifeEventService
  ) {
    super();
  }
  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.ngRedux.dispatch(setProxyValue(submittedAttemptProxyConnectPath, false));
  
    //first check if there is any events specified to creat the drop down;
    if(!this.entry?.custom?.qualifyingLifeEvents || !this.entry?.formControlAttributes?.formControlName){
      throw new Error("Error in form configs: No Life Event Options Specified or No formControl name defined!");
    }

    this.proxyLifeEventConnectPath = this.currentPage?.custom?.proxyLifeEventConnectPath ?? defaultLifeEventProxyConnectPath;

    //add proxy form control and event form to hold the event values
    this.form.addControl("SXEANG_PROXY_LIFE_EVENT_TRIGGER", this.fb.control(true));
    
    this.eventForm = this.fb.group<any>({
      [this.entry.formControlAttributes.formControlName]: this.fb.control<string>(null, {
        validators: Validators.required
      })
    });

    this.eventFormSubscription = this.eventForm.valueChanges.subscribe((value: any) => {
      if(this.eventForm.valid){
        this.forceParentEventValidity(true);
      } else {
        this.forceParentEventValidity(false);
      }
    });

    
    this.submitAttemptedObservable$ = this.ngRedux.select(['payloadStates', 'special-enrollment-circumstance', submittedAttemptProxyConnectPath]);
    this.submitAttemptedSubscription = this.submitAttemptedObservable$.subscribe((submitAttempted: boolean) => {
      if(submitAttempted){
        this.eventForm.controls[this.entry.formControlAttributes.formControlName].markAsTouched();
        this.eventForm.controls[this.entry.formControlAttributes.formControlName].updateValueAndValidity();
        if(this.eventForm.errors){
          this.forceParentEventValidity(false);
        }
      }
    })
    
    this.setupOptions();
  }

  ngAfterContentInit(): void {
    //clear this path onInit so we can make sure we aren't assigning old data when proceeding through the page
    // Initially ensure the form itself is invalid so that we cannot navigate until the proxy behavior has completed  
    // Timeout to get out of the forms-as-json initial validation
    setTimeout(() => {
      this.ngRedux.dispatch(setProxyValue(this.proxyLifeEventConnectPath, null));
      this.forceParentEventValidity(false)
    }, 0)
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.eventFormSubscription) {
      this.eventFormSubscription.unsubscribe();
    }

  }
  
  get eventFormControls(): ComponentFormControls{
    return this.eventForm?.controls as unknown as ComponentFormControls;
  }

  eventChanged(type: any, event: any){
    const payloadStates = this.reducerUtilityService.getPayloadStates();
    //assign event to proxy path each time so we know that it is appropriately being assigned the correct selected event
    payloadStates['special-enrollment-circumstance'][this.proxyLifeEventConnectPath] = event.value;
    this.eventForm.controls[this.entry?.formControlAttributes?.formControlName]?.updateValueAndValidity();
  }

  get componentFormControls(): ComponentFormControls {
    return this.formControls as unknown as ComponentFormControls;
  }

  /** Necessary to have VIEW reflect PROGRAMMATIC setting of values */
  compareWithFn(obj1: GetOpenEnrollmentRule, obj2: GetOpenEnrollmentRule) {
    return obj1 && obj2 && obj1.lifeEventId === obj2.lifeEventId;
  }

  async setupOptions() {
    if (this.entry?.custom?.fetchFromConfig) {
     this.qualifyingLifeEvents
        = (this.entry.custom.qualifyingLifeEvents as GetOpenEnrollmentRule[]).map(event => {
          event.asOfDate = this.dateService.getTodayAsMoment();
          return event;
        });
    } else {
      /** Get qualifying life event options */
      const response = await this.familyFormService.getOpenEnrollmentRules(this.entry?.custom?.lifeEventObj?.lifeEventId);
      const qualifyingLifeEvents: GetOpenEnrollmentRule[] = (response.responseData.dataElement.getOpenEnrollmentRules as GetOpenEnrollmentRule[]).map(event => {
          event.asOfDate = this.dateService.getTodayAsMoment();
          return event;
        });
    }
    
    this.ngRedux.dispatch(setFetchFormConfig(this.entry?.custom?.fetchFromConfig ?? true));
    this.sepLifeEventService.findLifeEventRule(this.qualifyingLifeEvents);
  }
  
  /**
   * Because we're using this component as a proxy, we need to ensure our actual parent formControl isn't processed separately.
   * Thus we bind its fate to that of the logic in this form:  if this form doesn't have a valid value, neither does the parent form
   * 
   * @param shouldForceValid False forces the control invalid
   */

  forceParentEventValidity(shouldForceValid: boolean){
    if(!shouldForceValid){
      this.form.controls[this.entry.formControlAttributes.formControlName].setErrors({ 'forced_invalid': true })
    } else {
      const currentErrors = this.form.controls[this.entry.formControlAttributes.formControlName].errors;
      if (currentErrors) {
        delete currentErrors['forced_invalid'];
      }
      this.form.controls[this.entry.formControlAttributes.formControlName].setErrors(currentErrors);
      this.form.controls[this.entry.formControlAttributes.formControlName].updateValueAndValidity();
    }
  }

  get fieldWidth(){
    if(this.entry?.custom?.minFieldWidth && this.entry?.custom?.maxFieldWidth){
      return `clamp(${this.entry?.custom?.minFieldWidth}, 35vw, ${this.entry?.custom?.maxFieldWidth})`
    } 
    return;
  }
}
